import React, {ReactElement, useEffect, useRef} from 'react';
import {SHAPE_COLOR_DEFAULT} from '../../../constants';
import {IElementEllipse} from '../../../incase-domain/elements/shapes/ellipse';

interface ElementEllipseComponentProps {
    element: IElementEllipse;
}

function ElementEllipseComponent({element}: ElementEllipseComponentProps): ReactElement {
    const {color, fill, isCircle, thickness = 0.5} = element;
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = React.useState(0);
    const [containerHeight, setContainerHeight] = React.useState(0);
    const [fontSizePx, setFontSizePx] = React.useState(16);

    const thicknessPx = fontSizePx * thickness;
    const rx = (containerWidth - thicknessPx) / 2;
    const ry = (containerHeight - thicknessPx) / 2;

    useEffect(() => {
        const observedElem = containerRef.current;
        const updateSize = (): void => {
            if (observedElem) {
                const rect = observedElem.getBoundingClientRect();
                setContainerWidth((prev) => (Math.abs(prev - rect.width) > 0.5 ? rect.width : prev));
                setContainerHeight((prev) => (Math.abs(prev - rect.height) > 0.5 ? rect.height : prev));
                const computedFontSize = window.getComputedStyle(observedElem).fontSize;
                setFontSizePx(parseFloat(computedFontSize));
            }
        };

        updateSize();

        let resizeObserver: ResizeObserver | null = null;
        if (observedElem && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => updateSize());
            resizeObserver.observe(observedElem);
        }

        return () => {
            if (resizeObserver && observedElem) {
                resizeObserver.unobserve(observedElem);
            }
        };
    }, []);

    if (isCircle) {
        const r = Math.min(rx, ry);
        return (
            <div ref={containerRef} className="c-circle">
                <svg
                    width="100%"
                    height="100%"
                    viewBox={`0 0 ${containerWidth} ${containerHeight}`}
                    style={{fontSize: '1em'}}
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <circle
                        cx={containerWidth / 2}
                        cy={containerHeight / 2}
                        r={r}
                        fill={fill ? color || SHAPE_COLOR_DEFAULT : 'none'}
                        stroke={color || SHAPE_COLOR_DEFAULT}
                        strokeWidth={`${thickness}em`}
                    />
                </svg>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="c-ellipse">
            <svg
                width="100%"
                height="100%"
                viewBox={`0 0 ${containerWidth} ${containerHeight}`}
                style={{fontSize: '1em'}}
                xmlns="http://www.w3.org/2000/svg"
            >
                <ellipse
                    cx={containerWidth / 2}
                    cy={containerHeight / 2}
                    rx={rx}
                    ry={ry}
                    fill={fill ? color || SHAPE_COLOR_DEFAULT : 'none'}
                    stroke={color || SHAPE_COLOR_DEFAULT}
                    strokeWidth={`${thickness}em`}
                />
            </svg>
        </div>
    );
}

export default ElementEllipseComponent;
